import { ArrowLeft, MessageCircle, Send, Ticket, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { Link, useSearchParams } from "react-router";

import { ApiError } from "../api/client";
import type { ChatDetail, ChatLine, ChatSummary, DirectoryMatch, TicketType } from "../api/types";
import { can, useMe } from "../auth/session";
import { formatDateTime } from "../lib/format";
import { useCatalogue } from "../tickets/api";
import { personChoice } from "../tickets/AssignTarget";
import { DirectoryPicker } from "../tickets/DirectoryPicker";
import { StatusBadge } from "../tickets/TicketBadges";
import { Avatar } from "../ui/Avatar";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Dialog } from "../ui/Dialog";
import { TextField } from "../ui/Field";
import { ErrorState, Notice, errorMessage } from "../ui/Notice";
import { Spinner } from "../ui/Spinner";
import { Select } from "../ui/Select";
import {
  useChat,
  useChatTicket,
  useChats,
  useEndChat,
  useMarkChatRead,
  useSendChatMessage,
  useStartChat,
  useStartChatWith,
  useTakeChat,
} from "./api";
import "./chat.css";

/**
 * Chat (DECISIONS #47): a drawer for quick conversations with support, apart from tickets.
 * Employees have one open chat at a time; admins answer them from an inbox and can turn a
 * chat into a ticket when it needs one. Admins and sub-admins can also start a chat with
 * anyone outside support (DECISIONS #50).
 */


interface ChatLauncher {
  openChat: () => void;
}

const ChatContext = createContext<ChatLauncher | null>(null);

export function useChatLauncher(): ChatLauncher {
  const launcher = useContext(ChatContext);
  if (!launcher) throw new Error("useChatLauncher needs ChatProvider");
  return launcher;
}

export function ChatProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  // Just opened: an employee with an open chat goes straight to it (once, not after Back).
  const [fresh, setFresh] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const [params, setParams] = useSearchParams();
  const linked = params.get("chat");

  const [followed, setFollowed] = useState<string | null>(null);
  if (linked && linked !== followed) {
    setFollowed(linked);
    setChatId(linked);
    setFresh(false);
    setOpen(true);
  }
  useEffect(() => {
    if (!linked) return;
    setParams(
      (current) => {
        current.delete("chat");
        return current;
      },
      { replace: true },
    );
  }, [linked, setParams]);

  const openChat = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) returnFocus.current = document.activeElement;
    setChatId(null);
    setFresh(true);
    setOpen(true);
  }, []);
  const show = useCallback((id: string | null) => {
    setFresh(false);
    setChatId(id);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
    returnFocus.current?.focus();
  }, []);
  const launcher = useMemo(() => ({ openChat }), [openChat]);

  return (
    <ChatContext.Provider value={launcher}>
      {children}
      {open ? <ChatDrawer chatId={chatId} fresh={fresh} onShow={show} onClose={close} /> : null}
    </ChatContext.Provider>
  );
}

/** The top-bar button: unread messages, and for admins chats waiting for a reply. */
export function ChatButton() {
  const me = useMe();
  const { openChat } = useChatLauncher();
  const chats = useChats();
  const unread = chats.data?.unread_total ?? 0;
  const label = unread ? `Chat, ${unread} unread` : "Chat";
  return (
    <button
      type="button"
      className="icon-btn"
      aria-label={label}
      data-tooltip={can(me, "use_workspace") ? "Chats" : "Chat with support"}
      data-tooltip-align="end"
      onClick={() => openChat()}
    >
      <MessageCircle size={20} aria-hidden="true" />
      {unread ? (
        <span className="bell-count" data-count={unread > 9 ? "9+" : String(unread)} aria-hidden="true" />
      ) : null}
    </button>
  );
}

function ChatDrawer({
  chatId,
  fresh,
  onShow,
  onClose,
}: {
  chatId: string | null;
  fresh: boolean;
  onShow: (id: string | null) => void;
  onClose: () => void;
}) {
  const me = useMe();
  const answers = can(me, "answer_chats");
  const titleId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  // The open chat's title, kept with its id so another chat never shows a stale one.
  const [title, setTitle] = useState<{ id: string; text: string } | null>(null);
  const onTitle = useCallback((id: string, text: string) => setTitle({ id, text }), []);

  useEffect(() => {
    heading.current?.focus();
  }, [chatId]);
  useEffect(() => {
    function onKeyDown(event: globalThis.KeyboardEvent) {
      // Escape in a dialog on top (e.g. Turn into a ticket) closes that, not the drawer.
      if (event.key === "Escape" && !document.querySelector("dialog[open]")) onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const staff = can(me, "use_workspace");
  const fallback = staff ? "Chats" : "Chat with support";
  return (
    <div className="chat-drawer" role="dialog" aria-labelledby={titleId}>
      <div className="chat-header">
        {chatId ? (
          <button type="button" className="icon-btn" aria-label="All chats" onClick={() => onShow(null)}>
            <ArrowLeft size={20} aria-hidden="true" />
          </button>
        ) : null}
        <h2 id={titleId} ref={heading} tabIndex={-1} className="chat-title">
          {chatId && title?.id === chatId ? title.text : fallback}
        </h2>
        <button type="button" className="icon-btn" aria-label="Close chat" onClick={onClose}>
          <X size={20} aria-hidden="true" />
        </button>
      </div>
      {chatId ? (
        <Conversation id={chatId} onTitle={onTitle} onBack={() => onShow(null)} onClose={onClose} />
      ) : answers ? (
        <AdminInbox onShow={onShow} />
      ) : (
        <EmployeeHome fresh={fresh} onShow={onShow} onClose={onClose} />
      )}
    </div>
  );
}

// --- Lists ---------------------------------------------------------------------------------

function EmployeeHome({
  fresh,
  onShow,
  onClose,
}: {
  fresh: boolean;
  onShow: (id: string) => void;
  onClose: () => void;
}) {
  const me = useMe();
  const chats = useChats();
  const items = chats.data?.items ?? [];
  const open = items.find((c) => c.mine && c.status !== "ended");
  const past = items.filter((c) => c.mine && c.status === "ended");
  // Sub-admins: chats they started with people (DECISIONS #50).
  const staff = can(me, "use_workspace");
  const handling = items.filter((c) => !c.mine);

  useEffect(() => {
    if (fresh && open && !staff) onShow(open.id);
  }, [fresh, open, staff, onShow]);

  if (chats.isPending) return <Spinner label="Loading chats" />;
  if (chats.isError) {
    return (
      <div className="chat-body">
        <ErrorState error={chats.error} onRetry={() => void chats.refetch()} />
      </div>
    );
  }
  return (
    <div className="chat-body">
      {staff ? (
        <section aria-labelledby="chat-handling">
          <NewChat onStarted={onShow} />
          <h3 id="chat-handling" className="chat-section">
            Your chats
          </h3>
          {handling.length ? (
            <ChatRows chats={handling} onShow={onShow} admin />
          ) : (
            <p className="muted chat-empty">No chats yet.</p>
          )}
        </section>
      ) : null}
      {open ? (
        <section aria-labelledby="chat-open">
          <h3 id="chat-open" className="chat-section">
            Your open chat
          </h3>
          <ChatRows chats={[open]} onShow={onShow} />
        </section>
      ) : can(me, "raise_ticket") ? (
        <StartChat onStarted={onShow} onClose={onClose} />
      ) : null}
      {past.length ? (
        <section aria-labelledby="chat-past">
          <h3 id="chat-past" className="chat-section">
            Previous chats
          </h3>
          <ChatRows chats={past} onShow={onShow} />
        </section>
      ) : null}
    </div>
  );
}

function AdminInbox({ onShow }: { onShow: (id: string) => void }) {
  const chats = useChats();
  if (chats.isPending) return <Spinner label="Loading chats" />;
  if (chats.isError) {
    return (
      <div className="chat-body">
        <ErrorState error={chats.error} onRetry={() => void chats.refetch()} />
      </div>
    );
  }
  const items = chats.data.items;
  const waiting = items.filter((c) => c.status === "waiting" && !c.mine);
  const active = items.filter((c) => c.status === "active" && !c.mine);
  const ended = items.filter((c) => c.status === "ended" && !c.mine);
  const own = items.filter((c) => c.mine && c.status !== "ended");
  return (
    <div className="chat-body">
      <NewChat onStarted={onShow} />
      <section aria-labelledby="chat-waiting">
        <h3 id="chat-waiting" className="chat-section">
          Waiting for a reply {waiting.length ? <span className="chat-section-count">{waiting.length}</span> : null}
        </h3>
        {waiting.length ? (
          <ChatRows chats={waiting} onShow={onShow} admin />
        ) : (
          <p className="muted chat-empty">No chats waiting.</p>
        )}
      </section>
      {active.length ? (
        <section aria-labelledby="chat-active">
          <h3 id="chat-active" className="chat-section">
            In progress
          </h3>
          <ChatRows chats={active} onShow={onShow} admin />
        </section>
      ) : null}
      {own.length ? (
        <section aria-labelledby="chat-own">
          <h3 id="chat-own" className="chat-section">
            Your own chat
          </h3>
          <ChatRows chats={own} onShow={onShow} />
        </section>
      ) : null}
      {ended.length ? (
        <section aria-labelledby="chat-ended">
          <h3 id="chat-ended" className="chat-section">
            Ended in the last two weeks
          </h3>
          <ChatRows chats={ended} onShow={onShow} admin />
        </section>
      ) : null}
    </div>
  );
}

function ChatRows({ chats, onShow, admin = false }: { chats: ChatSummary[]; onShow: (id: string) => void; admin?: boolean }) {
  const me = useMe();
  return (
    <ul className="chat-list">
      {chats.map((chat) => {
        // Admins see who's asking; the person asking sees who's helping.
        const name = admin ? chat.requester.name : (chat.assignee?.name ?? "Support");
        const handler =
          admin && chat.status === "active" && chat.assignee
            ? chat.assignee.id === me.id
              ? "You're handling this"
              : `${chat.assignee.name} is handling this`
            : null;
        return (
          <li key={chat.id}>
            <button type="button" className="chat-row" onClick={() => onShow(chat.id)}>
              <Avatar name={name} seed={admin ? chat.requester.id : (chat.assignee?.id ?? "support")} size="sm" />
              <span className="chat-row-text">
                <span className="chat-row-top">
                  <span className="cell-strong chat-row-title">
                    {admin ? chat.requester.name : chat.assignee ? `Chat with ${chat.assignee.name}` : "Chat with support"}
                  </span>
                  <time className="cell-secondary" dateTime={chat.last_message_at}>
                    {formatDateTime(chat.last_message_at)}
                  </time>
                </span>
                {admin && chat.requester_department ? (
                  <span className="chat-row-meta">{chat.requester_department}</span>
                ) : null}
                <span className="chat-row-preview">
                  {chat.last_from_me ? "You: " : ""}
                  {chat.preview}
                </span>
                {handler || chat.ticket ? (
                  <span className="chat-row-meta">
                    {chat.ticket ? <Badge tone="primary">Ticket {chat.ticket}</Badge> : handler}
                  </span>
                ) : null}
              </span>
              {chat.unread ? (
                <span className="chat-unread" aria-label={`${chat.unread} unread`}>
                  {chat.unread}
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Support starts a chat with someone outside support (DECISIONS #50): first in their chat panel. */
function NewChat({ onStarted }: { onStarted: (id: string) => void }) {
  const id = useId();
  const [person, setPerson] = useState<DirectoryMatch | null>(null);
  const [message, setMessage] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  // One id per message typed: a retry after a dropped connection doesn't start a second chat.
  const attempt = useRef(crypto.randomUUID());
  const start = useStartChatWith();

  function submit(event?: FormEvent) {
    event?.preventDefault();
    const text = message.trim();
    if (!person) return setProblem("Choose who to chat with.");
    if (!text) return setProblem("Write a message first.");
    if (start.isPending) return;
    setProblem(null);
    start.mutate(
      { person: personChoice(person), message: text, clientRequestId: attempt.current },
      {
        onSuccess: (chat) => {
          attempt.current = crypto.randomUUID();
          onStarted(chat.id);
        },
      },
    );
  }

  return (
    <form className="chat-start" aria-label="New chat" onSubmit={submit} noValidate>
      <DirectoryPicker label="To" hint="Anyone at Aspect outside support." value={person} onChange={setPerson} />
      <label htmlFor={`${id}-message`} className="field-label">
        Message
      </label>
      <textarea
        id={`${id}-message`}
        className="textarea"
        rows={3}
        maxLength={5000}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        onKeyDown={(event) => sendOnEnter(event, () => submit())}
      />
      {problem || start.isError ? <Notice tone="error">{problem ?? errorMessage(start.error)}</Notice> : null}
      <Button type="submit" loading={start.isPending}>
        <Send size={16} aria-hidden="true" />
        Send
      </Button>
    </form>
  );
}

function StartChat({ onStarted, onClose }: { onStarted: (id: string) => void; onClose: () => void }) {
  const id = useId();
  const [message, setMessage] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  // One id per message typed: a retry after a dropped connection doesn't start a second chat.
  const attempt = useRef(crypto.randomUUID());
  const start = useStartChat();

  function submit(event?: FormEvent) {
    event?.preventDefault();
    const text = message.trim();
    if (!text) {
      setProblem("Write your question first.");
      return;
    }
    if (start.isPending) return;
    setProblem(null);
    start.mutate(
      { message: text, clientRequestId: attempt.current },
      {
        onSuccess: (chat) => {
          attempt.current = crypto.randomUUID();
          onStarted(chat.id);
        },
        onError: (error) => {
          // Another tab already has a chat open: go to that one instead.
          const open = error instanceof ApiError && error.code === "chat_open" ? (error.details as { chat_id?: string }) : null;
          if (open?.chat_id) onStarted(open.chat_id);
        },
      },
    );
  }

  return (
    <form className="chat-start" onSubmit={submit} noValidate>
      <label htmlFor={`${id}-message`} className="cell-strong">
        Ask support a question
      </label>
      <p className="chat-start-hint">
        For quick questions. For faults,{" "}
        <Link to="/tickets/new" onClick={onClose}>
          raise a ticket
        </Link>
        .
      </p>
      <textarea
        id={`${id}-message`}
        className="textarea"
        rows={3}
        maxLength={5000}
        placeholder="e.g. How do I share my calendar with my team?"
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        onKeyDown={(event) => sendOnEnter(event, () => submit())}
      />
      {problem || (start.isError && !(start.error instanceof ApiError && start.error.code === "chat_open")) ? (
        <Notice tone="error">{problem ?? errorMessage(start.error)}</Notice>
      ) : null}
      <Button type="submit" loading={start.isPending}>
        <Send size={16} aria-hidden="true" />
        Send
      </Button>
    </form>
  );
}

// --- One chat ------------------------------------------------------------------------------

function Conversation({
  id,
  onTitle,
  onBack,
  onClose,
}: {
  id: string;
  onTitle: (id: string, title: string) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const me = useMe();
  const chat = useChat(id);
  const { mutate: markSeen } = useMarkChatRead();
  const list = useRef<HTMLDivElement>(null);
  const data = chat.data;
  // Answering: an admin looking at someone else's chat, or the staff member handling it.
  const answering = Boolean(
    data && data.requester.id !== me.id && (can(me, "answer_chats") || data.assignee?.id === me.id),
  );

  useEffect(() => {
    if (data) onTitle(data.id, answering ? data.requester.name : "Chat with support");
  }, [data, answering, onTitle]);

  // Seen: clear the unread count whenever new lines arrive while this is open.
  const lastSeen = useRef<string | null>(null);
  const newest = data?.messages.at(-1)?.id ?? null;
  useEffect(() => {
    if (!newest || newest === lastSeen.current) return;
    lastSeen.current = newest;
    markSeen(id);
    if (list.current) list.current.scrollTop = list.current.scrollHeight; // newest in view
  }, [newest, id, markSeen]);

  if (chat.isPending) return <Spinner label="Loading chat" />;
  if (chat.isError || !data) {
    return (
      <div className="chat-body">
        <ErrorState error={chat.error} onRetry={() => void chat.refetch()} />
      </div>
    );
  }

  return (
    <>
      <div className="chat-context">
        {answering ? <PersonCard chat={data} onClose={onClose} /> : null}
        <ChatState chat={data} answering={answering} onClose={onClose} />
      </div>
      <div ref={list} className="chat-messages" aria-live="polite" aria-relevant="additions">
        {data.messages.map((line) =>
          line.kind === "message" ? (
            <Bubble key={line.id} line={line} mine={line.author?.id === me.id} />
          ) : (
            <p key={line.id} className="chat-note">
              <ChatNote line={line} chat={data} meId={me.id} answering={answering} onClose={onClose} />{" "}
              <time dateTime={line.at}>{formatDateTime(line.at)}</time>
            </p>
          ),
        )}
      </div>
      {data.can.reply ? (
        <ChatComposer id={data.id} />
      ) : (
        <div className="chat-footer chat-ended">
          <p className="muted">
            {data.ticket ? "This chat continues on its ticket." : "This chat has ended."}
          </p>
          {!answering && can(me, "raise_ticket") ? (
            <Button size="sm" onClick={onBack}>
              Start a new chat
            </Button>
          ) : null}
        </div>
      )}
    </>
  );
}

function Bubble({ line, mine }: { line: ChatLine; mine: boolean }) {
  return (
    <div className={mine ? "bubble-row bubble-mine" : "bubble-row"}>
      <div className="bubble">
        {mine ? null : <span className="bubble-author">{line.author?.name ?? "Support"}</span>}
        <p className="bubble-text">{line.body}</p>
        <time className="bubble-time" dateTime={line.at}>
          {formatDateTime(line.at)}
        </time>
      </div>
    </div>
  );
}

/** A note about something that happened: who joined, took over, ended it, or made a ticket. */
function ChatNote({
  line,
  chat,
  meId,
  answering,
  onClose,
}: {
  line: ChatLine;
  chat: ChatDetail;
  meId: string;
  answering: boolean;
  onClose: () => void;
}) {
  const who = line.author ? (line.author.id === meId ? "You" : line.author.name) : null;
  switch (line.kind) {
    case "joined":
      return <>{who === "You" ? "You joined the chat." : answering ? `${who} joined the chat.` : `${who} from support joined the chat.`}</>;
    case "took_over":
      return <>{`${who} took over${line.data?.from ? ` from ${String(line.data.from)}` : ""}.`}</>;
    case "ticket": {
      const reference = String(line.data?.reference ?? chat.ticket ?? "");
      return (
        <>
          {who} turned this chat into ticket{" "}
          <Link to={`/tickets/${reference}`} onClick={onClose}>
            {reference}
          </Link>
          . Replies continue on the ticket.
        </>
      );
    }
    case "ended":
      if (!who) return <>{`The chat ended after ${String(line.data?.days ?? 7)} days without messages.`}</>;
      return <>{who === "You" ? "You ended the chat." : `${who} ended the chat.`}</>;
    default:
      return null;
  }
}

/** For admins: who this is, and what they already have open. */
function PersonCard({ chat, onClose }: { chat: ChatDetail; onClose: () => void }) {
  const person = chat.requester;
  const role = [person.job_title, person.department].filter(Boolean).join(", ");
  return (
    <div className="chat-person">
      <div className="chat-person-head">
        <Avatar name={person.name} seed={person.id} size="sm" />
        <div className="chat-person-text">
          <span className="cell-strong">{person.name}</span>
          <a className="chat-person-email" href={`mailto:${person.email}`}>
            {person.email}
          </a>
          {role ? <span className="chat-row-meta">{role}</span> : null}
        </div>
      </div>
      {chat.open_ticket_count ? (
        <div className="chat-person-tickets">
          <span className="chat-row-meta">
            {chat.open_ticket_count === 1 ? "1 open ticket" : `${chat.open_ticket_count} open tickets`}
          </span>
          <ul>
            {chat.open_tickets.map((t) => (
              <li key={t.reference}>
                <Link to={`/tickets/${t.reference}`} onClick={onClose} className="chat-person-ticket">
                  <span className="chat-person-ref">{t.reference}</span>
                  <span className="chat-row-title">{t.title}</span>
                  <StatusBadge status={t.status} />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <span className="chat-row-meta">No open tickets</span>
      )}
    </div>
  );
}

/** Where the chat stands, and what can be done next. */
function ChatState({ chat, answering, onClose }: { chat: ChatDetail; answering: boolean; onClose: () => void }) {
  const me = useMe();
  const end = useEndChat(chat.id);
  const take = useTakeChat(chat.id);
  const [makingTicket, setMakingTicket] = useState(false);
  const busy = end.isPending || take.isPending;

  let text: ReactNode;
  if (chat.status === "waiting") {
    text = answering
      ? `Waiting since ${formatDateTime(chat.created_at)}. Replying assigns it to you.`
      : "Waiting for support. Replies appear here.";
  } else if (chat.status === "active") {
    const name = chat.assignee?.name ?? "Support";
    text = answering
      ? chat.assignee?.id === me.id
        ? "You're handling this chat."
        : `${name} is handling this chat.`
      : `${name} from support is chatting with you.`;
  } else if (chat.ticket) {
    text = (
      <>
        This chat became ticket{" "}
        <Link to={`/tickets/${chat.ticket}`} onClick={onClose}>
          {chat.ticket}
        </Link>
        .
      </>
    );
  } else {
    text = "This chat has ended.";
  }

  const error = end.error ?? take.error;
  return (
    <div className="chat-state">
      <p className="chat-state-text">{text}</p>
      {chat.can.take || chat.can.make_ticket || chat.can.end ? (
        <div className="chat-actions">
          {chat.can.take ? (
            <Button size="sm" loading={take.isPending} disabled={busy} onClick={() => take.mutate(undefined)}>
              {chat.assignee ? "Take over" : "Take this chat"}
            </Button>
          ) : null}
          {chat.can.make_ticket ? (
            <Button size="sm" disabled={busy} onClick={() => setMakingTicket(true)}>
              <Ticket size={16} aria-hidden="true" />
              Turn into a ticket
            </Button>
          ) : null}
          {chat.can.end ? (
            <Button size="sm" variant="ghost" loading={end.isPending} disabled={busy} onClick={() => end.mutate(undefined)}>
              End chat
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? <Notice tone="error">{errorMessage(error)}</Notice> : null}
      {makingTicket ? <MakeTicketDialog chat={chat} onClose={() => setMakingTicket(false)} /> : null}
    </div>
  );
}

function MakeTicketDialog({ chat, onClose }: { chat: ChatDetail; onClose: () => void }) {
  const catalogue = useCatalogue();
  const firstMessage = chat.messages.find((m) => m.kind === "message")?.body ?? "";
  const firstLine = firstMessage.split("\n")[0]?.trim() ?? "";
  const [title, setTitle] = useState(firstLine.length > 200 ? `${firstLine.slice(0, 197)}…` : firstLine);
  const [type, setType] = useState<TicketType>("incident");
  const [serviceId, setServiceId] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const make = useChatTicket(chat.id);

  function submit() {
    if (title.trim().length < 5) {
      setProblem("Give the ticket a title of at least 5 characters.");
      return;
    }
    setProblem(null);
    make.mutate({ title: title.trim(), type, service_id: serviceId || null }, { onSuccess: onClose });
  }

  return (
    <Dialog
      open
      title="Turn this chat into a ticket"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" loading={make.isPending}>
            Create ticket
          </Button>
        </>
      }
    >
      <p className="muted">
        Copies the chat into a ticket for {chat.requester.name} and ends the chat.
      </p>
      <TextField
        label="Title"
        value={title}
        maxLength={200}
        autoFocus
        error={problem ?? undefined}
        onChange={(event) => setTitle(event.target.value)}
      />
      <div className="field">
        <label className="field-label" htmlFor="chat-ticket-type">
          Type
        </label>
        <Select
          id="chat-ticket-type"
          label="Type"
          value={type}
          onChange={(value) => setType(value as TicketType)}
          groups={[
            {
              options: [
                { value: "incident", label: "Incident" },
                { value: "service_request", label: "Service request" },
              ],
            },
          ]}
        />
      </div>
      <div className="field">
        <label className="field-label" htmlFor="chat-ticket-service">
          Service <span className="field-optional">optional</span>
        </label>
        <Select
          id="chat-ticket-service"
          label="Service"
          value={serviceId}
          onChange={setServiceId}
          groups={[
            { options: [{ value: "", label: "Not sure" }] },
            { options: (catalogue.data ?? []).map((s) => ({ value: s.id, label: s.name })) },
          ]}
        />
      </div>
      {make.isError ? <Notice tone="error">{errorMessage(make.error)}</Notice> : null}
    </Dialog>
  );
}

function ChatComposer({ id }: { id: string }) {
  const formId = useId();
  const [body, setBody] = useState("");
  // One id per message: a retry after a dropped connection isn't sent twice.
  const attempt = useRef(crypto.randomUUID());
  const send = useSendChatMessage(id);

  function submit(event?: FormEvent) {
    event?.preventDefault();
    const text = body.trim();
    if (!text || send.isPending) return;
    send.mutate(
      { body: text, clientRequestId: attempt.current },
      {
        onSuccess: () => {
          attempt.current = crypto.randomUUID();
          setBody("");
        },
      },
    );
  }

  return (
    <form className="chat-footer chat-composer" onSubmit={submit}>
      <label htmlFor={`${formId}-reply`} className="visually-hidden">
        Message
      </label>
      <textarea
        id={`${formId}-reply`}
        className="textarea"
        rows={2}
        maxLength={5000}
        placeholder="Write a message…"
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => sendOnEnter(event, () => submit())}
      />
      <Button type="submit" aria-label="Send message" loading={send.isPending}>
        <Send size={16} aria-hidden="true" />
      </Button>
      {send.isError ? <Notice tone="error">{errorMessage(send.error)}</Notice> : null}
    </form>
  );
}

/** Enter sends; Shift+Enter starts a new line (and IME composition is left alone). */
function sendOnEnter(event: KeyboardEvent<HTMLTextAreaElement>, sendIt: () => void) {
  if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
    event.preventDefault();
    sendIt();
  }
}
